import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { add } from "date-fns";
import { graphql } from "@repo/graphql-types/gql";
import { useFragment, type FragmentType } from "@repo/graphql-types";
import type { FollowUpAppointmentReminderInsertMutationMutation } from "@repo/graphql-types/graphql";
import { useNavigate, useRouteContext, useSearch } from "@tanstack/react-router";
import { logError, ServicePackageEnum } from "@repo/lib";
import { CheckCircle, InformationCircle } from "@repo/icons";
import { CpsAlert } from "corpus";
import { useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/form";
import type { SelectDrawerItemProps } from "@/components/select-drawer";
import { SelectInput } from "@/components/select-input";
import { AppointmentTypesSelectInput } from "@/components/appointment-types-select-input";
import { InputNumber } from "@/components/input-number";
import { trackEvent } from "@/lib/tracking.ts";
import {
  PageTitleRoot,
  PageTitleMainContent,
  PageTitleDescription,
} from "@/components/page-title/page-title-root";
import {
  MessageDrawerRoot,
  MessageDrawerTitle,
  MessageDrawerBody,
  MessageDrawerActions,
  MessageDrawerActionButton,
} from "@/components/message-drawer";
import { AssistProButton } from "@/routes/settings/assist/-components/assist-pro-button";
import { AssistFeatures } from "@/enums/assist-features";

const FollowUpAppointmentReminderInsertMutation = graphql(/* GraphQL */ `
  mutation FollowUpAppointmentReminderInsertMutation(
    $codAgendamento: Int!
    $codPaciente: Int!
    $codUsuario: Int
    $dataCadastro: datetime2!
    $codTipoLembrete: Int!
    $dataParaEnvio: datetime2!
    $codUsuarioCompromisso: Int!
  ) {
    insert_lembretes_one(
      object: {
        ativo: true
        codAgendamento: $codAgendamento
        codPaciente: $codPaciente
        codTipoLembrete: $codTipoLembrete
        codUsuario: $codUsuario
        dataCadastro: $dataCadastro
        dataParaEnvio: $dataParaEnvio
        codUsuarioCompromisso: $codUsuarioCompromisso
      }
    ) {
      codLembrete
      codUsuario
      usuarioCompromisso {
        codUsuarioCompromisso
        nome
      }
      codAgendamento
      dataParaEnvio
      paciente {
        nome
      }
      codTipoLembrete
    }
  }
`);

export const FollowUpAppointmentReminderFormFragment = graphql(/* GraphQL */ `
  fragment FollowUpAppointmentReminderFormFragment on query_root {
    usuariosAgendas(
      where: {
        ativo: { _eq: true }
        tbUsuariosAgendasCompromissos: {
          ativo: { _eq: true }
          UsuarioCompromisso: {
            usuariosCompromissosCanaisAgendamentos: {
              codCanalAgendamento: { _eq: $canalAgendamentoPaciente }
            }
            categoria: { _neq: $categoriaPeriodoGarantido }
          }
        }
        _or: [{ dataFim: { _gte: $dataInicio } }, { dataFim: { _is_null: true } }]
      }
    ) {
      usuariosAgendasCompromissos: tbUsuariosAgendasCompromissos {
        usuariosCompromissos: UsuarioCompromisso {
          ...AppointmentTypesListToSelectQueryFragment
          codUsuarioCompromisso
          nome
          duracao
        }
      }
    }

    appointment: agendamentos_by_pk(codAgendamento: $codAgendamento) {
      codPaciente
      codUsuario
      codAgendamento
    }
  }
`);

interface FollowUpAppointmentReminderFormProps {
  data: FragmentType<typeof FollowUpAppointmentReminderFormFragment>;
}

export const FollowUpAppointmentReminderForm = ({
  data,
}: FollowUpAppointmentReminderFormProps): JSX.Element => {
  const queryData = useFragment(FollowUpAppointmentReminderFormFragment, data);
  const { mutateAsync } = useGraphQLMutationWithErrorHandler(
    FollowUpAppointmentReminderInsertMutation,
  );
  const { queryClient, user } = useRouteContext({
    strict: false,
  });

  const navigate = useNavigate();

  const { action }: { action?: string } = useSearch({
    strict: false,
  });

  const isFollowUpAppointmentDisabled = !user.funcionalidadesContratadas.some(
    (item) =>
      item.codFuncionalidade === AssistFeatures.FollowUpAppointmentReminder.valueOf() &&
      item.ativo,
  );

  const FollowUpAppointmentReminderFormSchema = z
    .object({
      time: z.string(),
      period: z.number().min(1),
      appointmentTypeId: z.number().min(1),
    })
    .superRefine((values, context) => {
      if (!values.time) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "É necessário definir a data de envio",
          path: ["time"],
        });
      }
    });

  type FollowUpAppointmentReminderFormType = z.infer<
    typeof FollowUpAppointmentReminderFormSchema
  >;

  const form = useForm<FollowUpAppointmentReminderFormType>({
    resolver: zodResolver(FollowUpAppointmentReminderFormSchema),
    defaultValues: {
      time: "",
      period: 1,
      appointmentTypeId: 0,
    },
  });

  const onSubmit = async (
    formData: FollowUpAppointmentReminderFormType,
  ): Promise<void> => {
    const onSuccess = (
      responseData: FollowUpAppointmentReminderInsertMutationMutation,
    ): void => {
      trackEvent("Lembrete Criado", { ...responseData.insert_lembretes_one });

      void navigate({
        search: { action: "show-follow-up-appointment-reminder-drawer" },
        replace: true,
      });
    };
    const onError = (erro: Error): void => {
      logError(erro);
    };

    if (isFollowUpAppointmentDisabled) {
      const isLegacyUser = user.pacotesServicos.find(
        (servicePkg) =>
          servicePkg.codPacoteServicos === ServicePackageEnum.Legacy.valueOf(),
      );

      const subscribeUrl = isLegacyUser
        ? "/settings/assist/subscribe-assist-pro-legacy"
        : "/settings/assist/subscribe-assist-pro";

      return navigate({
        to: subscribeUrl,
        search: {
          navigateBack: true,
        },
      });
    }

    const appointment = queryData.appointment;
    if (!appointment?.codPaciente) {
      throw new Error("Appointment not found");
    }

    const now = new Date();
    let timeToAdd = {};

    if (formData.period === 1) {
      timeToAdd = { days: formData.time };
    } else if (formData.period === 30) {
      timeToAdd = { months: formData.time };
    }

    const variables = {
      codAgendamento: appointment.codAgendamento,
      codPaciente: appointment.codPaciente ?? 0,
      codUsuario: appointment.codUsuario,
      codTipoLembrete: 0,
      dataParaEnvio: add(now, timeToAdd).toISOString(),
      dataCadastro: now.toISOString(),
      codUsuarioCompromisso: formData.appointmentTypeId,
    };

    await mutateAsync(variables, { onSuccess, onError });
  };

  const periodInputs: SelectDrawerItemProps<number>[] = [
    { label: "dias", value: 1 },
    { label: "meses", value: 30 },
  ];

  const maxValue = form.watch("period") === 1 ? 365 : 12;
  const appointmentTypes = queryData.usuariosAgendas.flatMap((usuarioAgenda) =>
    usuarioAgenda.usuariosAgendasCompromissos.map(
      (usuariosAgendasCompromisso) => usuariosAgendasCompromisso.usuariosCompromissos,
    ),
  );

  const onCheckReminderListClick = (): void => {
    queryClient.removeQueries({
      queryKey: ["FollowUpAppointmentReminderPatientAppointmentQuery"],
    });

    void navigate({
      to: "/settings/assist/follow-up-appointment-reminder",
      replace: true,
    });
  };

  return (
    <>
      {isFollowUpAppointmentDisabled ? (
        <CpsAlert
          Icon={InformationCircle}
          type="info"
          title="Lembretes de retorno fazem parte do Assist Pro"
          description="Preencha para conhecer a funcionalidade. Para enviar de fato ao paciente, você
        precisa contratar o plano."
        />
      ) : null}

      <PageTitleRoot>
        <PageTitleMainContent>
          Configure o tempo para o envio automático de mensagens
        </PageTitleMainContent>
        <PageTitleDescription>
          Seu paciente será notificado no momento ideal para marcar uma consulta.
        </PageTitleDescription>
      </PageTitleRoot>

      <FormRoot {...form}>
        <FormHandlerSubmit handleSubmit={onSubmit} className="gap-6 pt-4 max-w-full">
          <div className="flex flex-col gap-1">
            <p className="font-medium text-sm text-neutral-500">
              Lembrar seu paciente em:
            </p>
            <div className="flex flex-row justify-between items-center gap-4">
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="time"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <InputNumber
                          title=" "
                          max={maxValue}
                          min="0"
                          placeholder="15"
                          {...field}
                          showArrowsControl={false}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="w-full">
                <FormField
                  control={form.control}
                  name="period"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <SelectInput
                          items={periodInputs}
                          title=" "
                          {...field}
                          placeholder="dias"
                          onChange={(value): void => {
                            const maxValueByType = value === 1 ? 365 : 12;
                            const timeValue = form.getValues("time");
                            if (Number(timeValue) > maxValueByType) {
                              form.setValue("time", maxValueByType.toString());
                            }
                            field.onChange(value);
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
          <div>
            <p className="font-medium text-sm text-neutral-500">
              Qual o tipo de atendimento?
            </p>
            <FormField
              control={form.control}
              name="appointmentTypeId"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AppointmentTypesSelectInput
                      title=" "
                      data={appointmentTypes}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <FormSubmitButton
            className="fixed w-full bottom-0 left-0 px-4 py-6"
            disabled={!form.formState.isValid}
            type="submit"
            as={AssistProButton}
          >
            Programar envio
          </FormSubmitButton>
        </FormHandlerSubmit>
      </FormRoot>
      <MessageDrawerRoot
        icon={CheckCircle}
        setOpen={() => true}
        open={action === "show-follow-up-appointment-reminder-drawer"}
        variant="secondary"
        dismissible={false}
      >
        <MessageDrawerTitle>Seu lembrete foi programado!</MessageDrawerTitle>
        <MessageDrawerBody>
          Na data determinada o paciente receberá o lembrete de novo agendamento.
        </MessageDrawerBody>
        <MessageDrawerActions>
          <MessageDrawerActionButton onClick={onCheckReminderListClick}>
            Ver lembretes
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
    </>
  );
};
